import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Phone, KeyRound, ArrowRight, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function OwnerLogin() {
  const { ownerLogin } = useAuth();
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!phone.trim() || !password) return;
    setError("");
    setLoading(true);
    try {
      await ownerLogin(phone.trim(), password);
      toast.success("Welcome back!");
      navigate("/owner/dashboard");
    } catch (err) {
      const msg = err.response?.data?.detail || "Unable to sign in. Please check your details.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-[calc(100vh-64px)] flex"
      style={{
        background: "linear-gradient(135deg, #F9F8F6 0%, #EDE9E3 100%)",
      }}
    >
      {/* Left panel — image */}
      <div className="hidden lg:block w-1/2 relative overflow-hidden bg-[#121315]">
        <img
          src="https://customer-assets-0z36b82j.emergentagent.net/job_floor-map-hub-2/artifacts/llxa96ma_Screen%20Shot%202026-08-14%20at%208.03.01%20AM.png"
          alt="Highline 32nd Avenue exterior in daylight"
          data-testid="owner-login-daytime-photo"
          className="absolute inset-0 h-full w-full object-cover"
          style={{ objectPosition: "50% 28%" }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F1115]/70 to-[#9C7C54]/30" />
        <div className="absolute bottom-0 left-0 right-0 p-12 text-white">
          <h2 className="font-heading text-4xl font-light leading-tight mb-3">
            Highline 32nd Avenue
          </h2>
          <p className="text-white/70 text-sm max-w-xs leading-relaxed">
            Owner-verified access to your property dashboard with full details and change request workflow.
          </p>
        </div>
        <figure
          data-testid="owner-login-nighttime-photo"
          className="absolute right-8 top-8 w-52 overflow-hidden border border-white/30 shadow-2xl"
        >
          <img
            src="https://customer-assets-0z36b82j.emergentagent.net/job_floor-map-hub-2/artifacts/5rvw7ozu_Screen%20Shot%202026-08-14%20at%208.03.18%20AM.webp"
            alt="32nd Avenue entrance illuminated at night"
            className="aspect-[4/3] w-full object-cover"
          />
          <figcaption className="bg-[#111315]/88 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-white">
            32nd Avenue after dark
          </figcaption>
        </figure>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="mb-10">
            <div className="text-xs tracking-[0.25em] uppercase text-[#9C7C54] font-semibold mb-3">
              Owner Portal
            </div>
            <h1 className="font-heading text-4xl font-light text-[#1A1A1A] mb-2">
              Sign in to your unit
            </h1>
            <p className="text-sm text-[#737373]">
              Enter the mobile number and password provided by the administrator.
            </p>
          </div>

          <form onSubmit={handleLogin} data-testid="owner-password-login-form" className="space-y-5">
              <div>
                <label className="block text-xs tracking-[0.15em] uppercase font-semibold text-[#737373] mb-2">
                  Mobile Number
                </label>
                <div className="relative">
                  <Phone size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9C7C54]" />
                  <input
                    data-testid="phone-input"
                    type="tel"
                    value={phone}
                    onChange={(e) => { setPhone(e.target.value); setError(""); }}
                    placeholder="e.g. 9819999999"
                    className="w-full pl-10 pr-4 py-3 border border-[#E5E5E5] dark:border-[#272A30] bg-white dark:bg-[#1A1D24] text-[#1A1A1A] dark:text-[#EAEAEA] text-sm focus:outline-none focus:ring-1 focus:ring-[#9C7C54] rounded-sm transition-all"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs tracking-[0.15em] uppercase font-semibold text-[#737373] mb-2">
                  Password
                </label>
                <div className="relative">
                  <KeyRound size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9C7C54]" />
                  <input
                    data-testid="owner-password-input"
                    type="password"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(""); }}
                    placeholder="Enter your password"
                    className="w-full pl-10 pr-4 py-3 border border-[#E5E5E5] dark:border-[#272A30] bg-white dark:bg-[#1A1D24] text-[#1A1A1A] dark:text-[#EAEAEA] text-sm focus:outline-none focus:ring-1 focus:ring-[#9C7C54] rounded-sm"
                    required
                  />
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 text-red-500 text-sm" data-testid="owner-login-error">
                  <AlertCircle size={14} />
                  <span>{error}</span>
                </div>
              )}

              <button
                data-testid="owner-login-submit-button"
                type="submit"
                disabled={loading || !phone || !password}
                className="w-full py-3 bg-[#9C7C54] text-white text-sm tracking-wide font-semibold hover:bg-[#7a5f3e] disabled:opacity-50 transition-colors rounded-sm flex items-center justify-center gap-2"
              >
                {loading ? (
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>Sign in <ArrowRight size={16} /></>
                )}
              </button>
          </form>

          {/* Admin login link */}
          <div className="mt-8 pt-6 border-t border-[#E5E5E5] dark:border-[#272A30] text-center">
            <a
              href="/admin"
              className="text-xs text-[#737373] hover:text-[#9C7C54] transition-colors"
              data-testid="admin-login-link"
            >
              Admin? Go to Admin Panel →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
